/**
 * Structure kinds (generators), member roles and editable parameter definitions.
 *
 * Default values are ILLUSTRATIVE starting points for a geometric model. They are not design values and carry no
 * claim of structural adequacy — every structure must be checked by a structural engineer.
 */
export const STRUCTURE_KINDS = [
  'ROOF_RAIL',
  'MINI_RAIL',
  'TILT_FRAME',
  'ELEVATED_ROW',
  'ELEVATED_MULTI',
  'PORTAL',
  'COLUMN_BEAM',
  'CUSTOM',
  'CARPORT',
] as const;
export type StructureKind = (typeof STRUCTURE_KINDS)[number];

export const STRUCTURE_KIND_LABEL: Record<StructureKind, string> = {
  ROOF_RAIL: 'Ray song song mái',
  MINI_RAIL: 'MiniRail thấp cho mái tôn',
  TILT_FRAME: 'Khung nghiêng điều chỉnh (mái bằng)',
  ELEVATED_ROW: 'Khung thép nâng một hàng',
  ELEVATED_MULTI: 'Khung thép nâng nhiều hàng',
  PORTAL: 'Khung cổng lặp lại (C&I)',
  COLUMN_BEAM: 'Kết cấu cột – dầm',
  CUSTOM: 'Kết cấu tự dựng',
  CARPORT: 'Nhà để xe (khung độc lập, tấm pin trên mái)',
};

export const MEMBER_ROLES = [
  'COLUMN',
  'POST',
  'BEAM',
  'RAFTER',
  'PURLIN',
  'RAIL',
  'BRACE',
  'BASE_RAIL',
  'FOOT',
  'CUSTOM',
] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export const MEMBER_ROLE_LABEL: Record<MemberRole, string> = {
  COLUMN: 'Cột',
  POST: 'Cột chống',
  BEAM: 'Dầm',
  RAFTER: 'Kèo / xà nghiêng',
  PURLIN: 'Xà gồ',
  RAIL: 'Rail',
  BRACE: 'Giằng',
  BASE_RAIL: 'Thanh đế',
  FOOT: 'Chân đỡ / móc mái',
  CUSTOM: 'Cấu kiện thêm',
};

export type ParamValue = number | string | boolean;
export type ParamGroup =
  | 'Bố trí'
  | 'Hình học'
  | 'Khung'
  | 'Xà gồ / rail'
  | 'Lắp đặt tấm pin'
  | 'Giằng'
  | 'Chân cột'
  | 'Khối lượng';

export interface ParamDef {
  key: string;
  label: string;
  group: ParamGroup;
  type: 'number' | 'select' | 'boolean';
  unit?: 'mm' | '°' | '%' | 'cái';
  min?: number;
  max?: number;
  step?: number;
  options?: { value: string; label: string }[];
  help?: string;
  /** Show only when another parameter has one of these values */
  showIf?: { key: string; in: ParamValue[] };
}

const n = (
  key: string,
  label: string,
  group: ParamGroup,
  unit: ParamDef['unit'],
  min: number,
  max: number,
  step = 1,
  extra: Partial<ParamDef> = {},
): ParamDef => ({ key, label, group, type: 'number', unit, min, max, step, ...extra });
const sel = (
  key: string,
  label: string,
  group: ParamGroup,
  options: [string, string][],
  extra: Partial<ParamDef> = {},
): ParamDef => ({
  key,
  label,
  group,
  type: 'select',
  options: options.map(([value, l]) => ({ value, label: l })),
  ...extra,
});
const bool = (key: string, label: string, group: ParamGroup, extra: Partial<ParamDef> = {}): ParamDef => ({
  key,
  label,
  group,
  type: 'boolean',
  ...extra,
});

const MANUAL = { showIf: { key: 'sizeMode', in: ['MANUAL'] } };

/**
 * Direction the tilted panels face. ROOF / OPPOSITE keep the rows of the roof layout; LEFT / RIGHT / AZIMUTH turn the
 * tilt (and the rows of an installation zone carrying the structure) to another compass direction.
 */
export const TILT_DIRS: [string, string][] = [
  ['ROOF', 'Theo hướng mái (tấm quay về phía mái nhìn)'],
  ['OPPOSITE', 'Ngược hướng mái (tấm quay về phía đỉnh mái)'],
  ['LEFT', 'Lệch 90° sang trái hướng mái'],
  ['RIGHT', 'Lệch 90° sang phải hướng mái'],
  ['AZIMUTH', 'Theo hướng nhập (độ la bàn)'],
];
const P_TILT_AZIMUTH = n('tiltAzimuthDeg', 'Hướng tấm quay về (0° Bắc, 90° Đông, 180° Nam, 270° Tây)', 'Hình học', '°', 0, 360, 1, {
  showIf: { key: 'tiltDir', in: ['AZIMUTH'] },
});
const TILT = { showIf: { key: 'tiltMode', in: ['TILT'] } };

const P_PLATES: ParamDef[] = [
  n('plateWMm', 'Bản mã — rộng', 'Chân cột', 'mm', 50, 1000, 10),
  n('plateLMm', 'Bản mã — dài', 'Chân cột', 'mm', 50, 1000, 10),
  n('plateTMm', 'Bản mã — dày', 'Chân cột', 'mm', 3, 60, 1),
  sel('anchorCount', 'Số bu lông neo / bản mã', 'Chân cột', [
    ['2', '2'],
    ['4', '4'],
    ['6', '6'],
  ]),
  n('anchorDiaMm', 'Đường kính bu lông neo', 'Chân cột', 'mm', 6, 48, 1),
  n('anchorEdgeMm', 'Khoảng cách bu lông tới mép bản mã', 'Chân cột', 'mm', 15, 300, 5),
];
const P_COMMON_END: ParamDef[] = [
  n('wastePct', 'Hao hụt mua hàng (thép)', 'Khối lượng', '%', 0, 50, 0.5, {
    help: 'Chỉ cộng vào số lượng mua, không đổi khối lượng tính từ mô hình.',
  }),
  n('clampZoneMinPct', 'Vùng kẹp cho phép — từ', 'Xà gồ / rail', '%', 0, 50, 1, {
    help: 'Theo datasheet tấm pin, tính từ mép tấm (0 = chưa khai báo, không kiểm tra).',
  }),
  n('clampZoneMaxPct', 'Vùng kẹp cho phép — đến', 'Xà gồ / rail', '%', 0, 50, 1),
];
/** Physical module ↔ support relationship (see mounting-config.ts for which values each kind allows). */
const P_MODULE_SIDE = sel('moduleSide', 'Cấu hình lắp đặt', 'Lắp đặt tấm pin', [
  ['ABOVE', 'Tấm pin phía trên thanh đỡ'],
  ['BELOW', 'Tấm pin phía dưới thanh đỡ (lắp treo)'],
]);
const P_MODULE_GAP = n('moduleRailGapMm', 'Khoảng hở tấm – thanh đỡ (đệm kẹp)', 'Lắp đặt tấm pin', 'mm', 0, 100, 1, {
  help: 'Khoảng cách từ mặt thanh đỡ tới mặt tấm pin theo hệ kẹp đã chọn.',
});
const P_MOUNT_FRAME: ParamDef[] = [
  P_MODULE_SIDE,
  sel('railLayer', 'Lớp đỡ tấm pin', 'Lắp đặt tấm pin', [
    ['NONE', 'Kẹp trực tiếp lên xà gồ'],
    ['RAIL', 'Rail vuông góc xà gồ'],
    ['MINI_RAIL', 'Mini rail trên xà gồ'],
  ]),
  n('railCrossInsetPct', 'Vị trí rail so với mép tấm (theo bề ngang tấm)', 'Lắp đặt tấm pin', '%', 5, 45, 1, {
    showIf: { key: 'railLayer', in: ['RAIL'] },
  }),
  n('railCrossOverhangMm', 'Rail vươn ra ngoài tấm', 'Lắp đặt tấm pin', 'mm', 0, 500, 5, {
    showIf: { key: 'railLayer', in: ['RAIL'] },
  }),
  n('miniRailLengthMm', 'Chiều dài mỗi đoạn mini rail', 'Lắp đặt tấm pin', 'mm', 100, 1000, 10, {
    showIf: { key: 'railLayer', in: ['MINI_RAIL'] },
  }),
  P_MODULE_GAP,
];
const P_SUPPORT_LINES: ParamDef[] = [
  sel('purlinMode', 'Bố trí xà gồ', 'Xà gồ / rail', [
    ['AUTO_ROWS', 'Theo hàng tấm (2 thanh / hàng)'],
    ['SPACING', 'Theo bước đều'],
  ]),
  n('railInsetPct', 'Vị trí xà gồ so với mép tấm', 'Xà gồ / rail', '%', 5, 45, 1, {
    showIf: { key: 'purlinMode', in: ['AUTO_ROWS'] },
  }),
  n('purlinSpacingMm', 'Bước xà gồ', 'Xà gồ / rail', 'mm', 300, 3000, 10, {
    showIf: { key: 'purlinMode', in: ['SPACING'] },
  }),
  n('purlinOverhangMm', 'Xà gồ vươn ra ngoài tấm', 'Xà gồ / rail', 'mm', 0, 1000, 10),
];
const P_PLANE: ParamDef[] = [
  sel('sizeMode', 'Kích thước khung', 'Bố trí', [
    ['PANELS', 'Theo dãy tấm pin của mái'],
    ['MANUAL', 'Nhập kích thước'],
    ['ZONE', 'Theo vùng lắp tấm (kéo vùng để đổi kích thước)'],
  ]),
  n('lengthMm', 'Chiều dài khung (dọc hàng tấm)', 'Bố trí', 'mm', 1000, 300_000, 100, MANUAL),
  n('widthMm', 'Chiều rộng khung (ngang hàng tấm)', 'Bố trí', 'mm', 1000, 200_000, 100, MANUAL),
  n('offsetUMm', 'Dịch theo dọc hàng', 'Bố trí', 'mm', -100_000, 100_000, 100, MANUAL),
  n('offsetVMm', 'Dịch theo ngang hàng', 'Bố trí', 'mm', -100_000, 100_000, 100, MANUAL),
  n('edgeMarginMm', 'Mặt khung rộng hơn dãy tấm', 'Bố trí', 'mm', 0, 3000, 10),
  sel('tiltMode', 'Mặt đỡ tấm pin', 'Hình học', [
    ['TILT', 'Nghiêng theo góc nhập'],
    ['ROOF', 'Song song mặt mái'],
  ]),
  n('tiltDeg', 'Góc nghiêng tấm pin', 'Hình học', '°', 0, 60, 0.5, TILT),
  sel('tiltDir', 'Hướng nghiêng tấm', 'Hình học', TILT_DIRS, TILT),
  P_TILT_AZIMUTH,
  n('heightMm', 'Chiều cao cột phía trước (tới đáy dầm)', 'Hình học', 'mm', 100, 30_000, 50, {
    help: 'Đo từ mặt mái tới đáy cấu kiện đỡ chính ở hàng cột thấp.',
  }),
];

const PARAMS: Record<StructureKind, ParamDef[]> = {
  ROOF_RAIL: [
    n('railsPerRow', 'Số rail mỗi hàng tấm', 'Xà gồ / rail', 'cái', 2, 4, 1),
    n('railInsetPct', 'Vị trí rail so với mép tấm', 'Xà gồ / rail', '%', 5, 45, 1),
    n('standoffMm', 'Chiều cao chân đỡ (mái → đáy rail)', 'Hình học', 'mm', 0, 500, 5),
    n('railOverhangMm', 'Rail vươn ra ngoài tấm', 'Xà gồ / rail', 'mm', 0, 500, 5),
    n('supportSpacingMm', 'Khoảng cách chân đỡ tối đa', 'Khung', 'mm', 200, 3000, 50),
    n('endCantileverMm', 'Khoảng công-xôn đầu rail', 'Khung', 'mm', 0, 1500, 10),
    n('rowBreakMm', 'Khe ngắt rail khi hàng tấm bị gián đoạn', 'Bố trí', 'mm', 100, 10_000, 50),
    P_MODULE_SIDE,
    P_MODULE_GAP,
    ...P_COMMON_END,
  ],
  MINI_RAIL: [
    n('railInsetPct', 'Vị trí MiniRail so với mép tấm', 'Xà gồ / rail', '%', 5, 45, 1),
    n('miniRailLengthMm', 'Chiều dài mỗi đoạn MiniRail', 'Xà gồ / rail', 'mm', 100, 1000, 10),
    n('rowBreakMm', 'Khe ngắt khi hàng tấm bị gián đoạn', 'Bố trí', 'mm', 100, 10_000, 50),
    P_MODULE_SIDE,
    P_MODULE_GAP,
    ...P_COMMON_END,
  ],
  TILT_FRAME: [
    n('tiltDeg', 'Góc nghiêng tấm pin', 'Hình học', '°', 0, 60, 0.5),
    sel('tiltDir', 'Hướng nghiêng tấm', 'Hình học', TILT_DIRS),
    P_TILT_AZIMUTH,
    n('heightMm', 'Chiều cao chân trước (mái → đáy kèo)', 'Hình học', 'mm', 50, 3000, 10),
    n('frameSpacingMm', 'Bước khung', 'Khung', 'mm', 300, 4000, 50),
    n('endCantileverMm', 'Công-xôn hai đầu dãy', 'Khung', 'mm', 0, 1500, 10),
    n('frontInsetMm', 'Chân trước lùi vào từ mép trước tấm', 'Khung', 'mm', 0, 1500, 10),
    n('rearInsetMm', 'Chân sau lùi vào từ mép sau tấm', 'Khung', 'mm', 0, 1500, 10),
    bool('baseRail', 'Có thanh đế (khung đặt đối trọng / không khoan mái)', 'Khung'),
    sel('bracing', 'Giằng khung', 'Giằng', [
      ['NONE', 'Không'],
      ['DIAG', 'Giằng chéo mỗi khung'],
    ]),
    n('rowBreakMm', 'Khe ngắt khung khi hàng tấm bị gián đoạn', 'Bố trí', 'mm', 100, 10_000, 50),
    ...P_SUPPORT_LINES.filter((p) => p.key !== 'purlinMode' && p.key !== 'purlinSpacingMm'),
    ...P_MOUNT_FRAME,
    ...P_PLATES,
    ...P_COMMON_END,
  ],
  ELEVATED_ROW: [
    n('tiltDeg', 'Góc nghiêng tấm pin', 'Hình học', '°', 0, 60, 0.5),
    sel('tiltDir', 'Hướng nghiêng tấm', 'Hình học', TILT_DIRS),
    P_TILT_AZIMUTH,
    n('heightMm', 'Chiều cao cột phía trước (tới đáy kèo)', 'Hình học', 'mm', 300, 20_000, 50),
    sel('columnLayout', 'Số cột mỗi khung', 'Khung', [
      ['TWO', '2 cột (trước / sau)'],
      ['ONE', '1 cột giữa (chữ T)'],
    ]),
    n('frameSpacingMm', 'Bước khung (khoảng cách cột dọc hàng)', 'Khung', 'mm', 1000, 12_000, 100),
    n('endCantileverMm', 'Công-xôn hai đầu dãy', 'Khung', 'mm', 0, 3000, 50),
    n('frontInsetMm', 'Cột trước lùi vào từ mép trước tấm', 'Khung', 'mm', 0, 3000, 10),
    n('rearInsetMm', 'Cột sau lùi vào từ mép sau tấm', 'Khung', 'mm', 0, 3000, 10),
    bool('kneeBrace', 'Có giằng góc cột – kèo', 'Giằng'),
    n('kneeLengthMm', 'Chiều dài chân giằng góc (theo cột)', 'Giằng', 'mm', 100, 3000, 50, {
      showIf: { key: 'kneeBrace', in: [true] },
    }),
    sel('bracing', 'Giằng dọc hàng cột', 'Giằng', [
      ['NONE', 'Không'],
      ['END_BAYS', 'Giằng chéo hai nhịp đầu'],
      ['ALL_BAYS', 'Giằng chéo mọi nhịp'],
    ]),
    n('rowBreakMm', 'Khe ngắt khung khi hàng tấm bị gián đoạn', 'Bố trí', 'mm', 100, 10_000, 50),
    ...P_SUPPORT_LINES.filter((p) => p.key !== 'purlinMode' && p.key !== 'purlinSpacingMm'),
    ...P_MOUNT_FRAME,
    ...P_PLATES,
    ...P_COMMON_END,
  ],
  ELEVATED_MULTI: [
    ...P_PLANE,
    n('columnSpacingUMm', 'Khoảng cách cột dọc hàng tấm', 'Khung', 'mm', 1000, 20_000, 100),
    n('columnSpacingVMm', 'Khoảng cách cột ngang hàng tấm', 'Khung', 'mm', 1000, 20_000, 100),
    n('cantileverUMm', 'Công-xôn đầu khung (dọc)', 'Khung', 'mm', 0, 5000, 50),
    n('cantileverVMm', 'Công-xôn trước / sau (ngang)', 'Khung', 'mm', 0, 5000, 50),
    n('rafterSpacingMm', 'Bước kèo (trên dầm chính)', 'Khung', 'mm', 500, 6000, 50),
    bool('kneeBrace', 'Có giằng góc cột – dầm', 'Giằng'),
    n('kneeLengthMm', 'Chiều dài chân giằng góc (theo cột)', 'Giằng', 'mm', 100, 3000, 50, {
      showIf: { key: 'kneeBrace', in: [true] },
    }),
    sel('bracing', 'Giằng chéo giữa các cột', 'Giằng', [
      ['NONE', 'Không'],
      ['END_BAYS', 'Hai nhịp đầu mỗi phương'],
      ['ALL_BAYS', 'Mọi nhịp'],
    ]),
    ...P_SUPPORT_LINES,
    ...P_MOUNT_FRAME,
    ...P_PLATES,
    ...P_COMMON_END,
  ],
  PORTAL: [
    ...P_PLANE,
    n('frameSpacingMm', 'Bước khung cổng', 'Khung', 'mm', 1500, 15_000, 100),
    n('frameCount', 'Số khung (0 = tự tính theo bước)', 'Khung', 'cái', 0, 200, 1),
    n('cantileverUMm', 'Công-xôn đầu khung (dọc)', 'Khung', 'mm', 0, 5000, 50),
    n('cantileverVMm', 'Công-xôn trước / sau (ngang)', 'Khung', 'mm', 0, 5000, 50),
    bool('kneeBrace', 'Có giằng góc (vai khung)', 'Giằng'),
    n('kneeLengthMm', 'Chiều dài chân giằng góc (theo cột)', 'Giằng', 'mm', 100, 3000, 50, {
      showIf: { key: 'kneeBrace', in: [true] },
    }),
    sel('bracing', 'Giằng chéo mặt mái & hàng cột', 'Giằng', [
      ['NONE', 'Không'],
      ['END_BAYS', 'Hai nhịp đầu'],
      ['ALL_BAYS', 'Mọi nhịp'],
    ]),
    ...P_SUPPORT_LINES,
    ...P_MOUNT_FRAME,
    ...P_PLATES,
    ...P_COMMON_END,
  ],
  COLUMN_BEAM: [
    ...P_PLANE,
    n('columnSpacingUMm', 'Khoảng cách cột dọc hàng tấm', 'Khung', 'mm', 1000, 20_000, 100),
    n('columnSpacingVMm', 'Khoảng cách cột ngang hàng tấm', 'Khung', 'mm', 1000, 20_000, 100),
    n('cantileverUMm', 'Công-xôn đầu khung (dọc)', 'Khung', 'mm', 0, 5000, 50),
    n('cantileverVMm', 'Công-xôn trước / sau (ngang)', 'Khung', 'mm', 0, 5000, 50),
    sel('bracing', 'Giằng chéo giữa các cột', 'Giằng', [
      ['NONE', 'Không'],
      ['END_BAYS', 'Hai nhịp đầu mỗi phương'],
      ['ALL_BAYS', 'Mọi nhịp'],
    ]),
    ...P_SUPPORT_LINES,
    ...P_MOUNT_FRAME,
    ...P_PLATES,
    ...P_COMMON_END,
  ],
  CUSTOM: [
    ...P_PLANE,
    sel('start', 'Khởi tạo', 'Khung', [
      ['EMPTY', 'Trống — tự thêm cấu kiện'],
      ['FRAME4', '4 cột góc + dầm biên'],
    ]),
    n('panelOffsetMm', 'Tấm pin cách đáy mặt khung (khi khởi tạo trống)', 'Hình học', 'mm', 0, 2000, 10, {
      showIf: { key: 'start', in: ['EMPTY'] },
    }),
    ...P_SUPPORT_LINES.map((d) => (d.showIf ? d : { ...d, showIf: { key: 'start', in: ['FRAME4'] } })),
    ...P_MOUNT_FRAME.map((d) => (d.showIf ? d : { ...d, showIf: { key: 'start', in: ['FRAME4'] } })),
    ...P_PLATES,
    ...P_COMMON_END,
  ],
  CARPORT: [
    n('centerXMm', 'Tâm nhà để xe — X (mặt bằng)', 'Bố trí', 'mm', -2_000_000, 2_000_000, 10),
    n('centerYMm', 'Tâm nhà để xe — Y (mặt bằng)', 'Bố trí', 'mm', -2_000_000, 2_000_000, 10),
    n('azimuthDeg', 'Hướng mặt trước', 'Bố trí', '°', 0, 359.9, 1, {
      help: 'Độ dốc dương: mặt trước là phía thấp. Độ dốc âm: mặt trước là phía cao.',
    }),
    n('lengthMm', 'Chiều dài (giữa hai khung đầu)', 'Hình học', 'mm', 2000, 200_000, 50),
    n('widthMm', 'Chiều rộng (giữa hai hàng cột / trục tham chiếu)', 'Hình học', 'mm', 500, 40_000, 50),
    n('parkingBays', 'Số chỗ đỗ xe', 'Bố trí', 'cái', 1, 100, 1),
    sel('roofType', 'Kiểu mái', 'Hình học', [
      ['MONO', 'Một dốc'],
      ['GABLE', 'Hai dốc'],
    ]),
    n('slopeDeg', 'Độ dốc mái', 'Hình học', '°', -45, 45, 0.5, {
      help: 'Góc giữa cột và kèo = 90° + độ dốc. Dương: một dốc thấp phía trước / hai dốc nóc giữa. Âm: một dốc cao phía trước / hai dốc chữ V (bướm), thoát nước giữa.',
    }),
    n('heightMm', 'Chiều cao tới đáy dầm tại trục trước', 'Hình học', 'mm', 1800, 12_000, 10, {
      help: 'Đo từ mặt đất tới đáy dầm dọc ở trục tham chiếu phía trước (y = rộng / 2); hai dốc: ở hai trục ±rộng / 2.',
    }),
    n('frontOverhangMm', 'Mái vươn phía trước (công-xôn)', 'Hình học', 'mm', 0, 4000, 10),
    n('rearOverhangMm', 'Mái vươn phía sau (công-xôn)', 'Hình học', 'mm', 0, 4000, 10),
    n('sideOverhangMm', 'Mái vươn hai đầu (xà gồ)', 'Hình học', 'mm', 0, 3000, 10),
    sel('columnLayout', 'Bố trí cột', 'Khung', [
      ['TWO', '2 hàng cột (trước / sau)'],
      ['BACK', '1 hàng cột phía sau (công-xôn)'],
      ['CENTER', '1 hàng cột giữa (chữ T)'],
    ]),
    sel('frameMode', 'Bước khung', 'Khung', [
      ['PER_BAYS', 'Theo chỗ đỗ xe'],
      ['SPACING', 'Theo bước tối đa'],
    ]),
    n('baysPerFrame', 'Số chỗ đỗ giữa hai khung', 'Khung', 'cái', 1, 10, 1, { showIf: { key: 'frameMode', in: ['PER_BAYS'] } }),
    n('frameSpacingMm', 'Bước khung tối đa', 'Khung', 'mm', 1000, 15_000, 50, { showIf: { key: 'frameMode', in: ['SPACING'] } }),
    bool('kneeBrace', 'Có giằng góc cột – dầm', 'Giằng'),
    n('kneeLengthMm', 'Chiều dài chân giằng góc (theo cột)', 'Giằng', 'mm', 100, 2000, 50, {
      showIf: { key: 'kneeBrace', in: [true] },
    }),
    bool('stayRod', 'Cột vươn trên mái + thanh căng xuống kèo', 'Giằng', {
      help: 'Đỉnh cột kéo dài trên mái, thanh căng xiên tới phần công-xôn mỗi phía không có hàng cột khác.',
    }),
    n('stayMastMm', 'Cột vươn trên mặt kèo', 'Giằng', 'mm', 300, 5000, 50, { showIf: { key: 'stayRod', in: [true] } }),
    n('stayAtPct', 'Điểm neo thanh căng (% công-xôn tính từ cột)', 'Giằng', '%', 20, 100, 5, {
      showIf: { key: 'stayRod', in: [true] },
    }),
    sel('bracing', 'Giằng chéo dọc hàng cột', 'Giằng', [
      ['NONE', 'Không'],
      ['END_BAYS', 'Hai nhịp đầu'],
      ['ALL_BAYS', 'Mọi nhịp'],
    ]),
    n('clearHeightMinMm', 'Chiều cao thông thủy tối thiểu (0 = không kiểm tra)', 'Hình học', 'mm', 0, 6000, 10, {
      help: 'Giá trị do dự án đặt; kiểm tra điểm thấp nhất của dầm / tấm pin so với mặt đất.',
    }),
    ...P_SUPPORT_LINES,
    ...P_MOUNT_FRAME,
    ...P_PLATES,
    ...P_COMMON_END,
  ],
};

const PLATES = {
  plateWMm: 200,
  plateLMm: 200,
  plateTMm: 10,
  anchorCount: '4',
  anchorDiaMm: 12,
  anchorEdgeMm: 40,
};
// Khung mặc định bằng đúng kích thước dãy tấm: xà gồ không vươn, mặt khung không rộng hơn dãy tấm
const SUPPORT = { purlinMode: 'AUTO_ROWS', railInsetPct: 25, purlinSpacingMm: 1100, purlinOverhangMm: 0 };
const COMMON = { wastePct: 0, clampZoneMinPct: 0, clampZoneMaxPct: 0 };
const MOUNT = {
  moduleSide: 'ABOVE',
  railLayer: 'NONE',
  railCrossInsetPct: 20,
  railCrossOverhangMm: 50,
  miniRailLengthMm: 300,
  moduleRailGapMm: 0,
};
const PLANE = {
  sizeMode: 'PANELS',
  lengthMm: 12_000,
  widthMm: 6000,
  offsetUMm: 0,
  offsetVMm: 0,
  edgeMarginMm: 0,
  tiltMode: 'TILT',
  tiltDeg: 10,
  tiltDir: 'ROOF',
  tiltAzimuthDeg: 180,
  heightMm: 2500,
};

/** Default parameter values per kind (illustrative, see file header). */
export const DEFAULT_PARAMS: Record<StructureKind, Record<string, ParamValue>> = {
  ROOF_RAIL: {
    railsPerRow: 2,
    railInsetPct: 25,
    standoffMm: 40,
    railOverhangMm: 50,
    supportSpacingMm: 1200,
    endCantileverMm: 200,
    rowBreakMm: 600,
    moduleSide: 'ABOVE',
    moduleRailGapMm: 0,
    ...COMMON,
  },
  MINI_RAIL: { railInsetPct: 25, miniRailLengthMm: 300, rowBreakMm: 600, moduleSide: 'ABOVE', moduleRailGapMm: 0, ...COMMON },
  TILT_FRAME: {
    tiltDeg: 15,
    tiltDir: 'ROOF',
    tiltAzimuthDeg: 180,
    heightMm: 300,
    frameSpacingMm: 1500,
    endCantileverMm: 300,
    frontInsetMm: 150,
    rearInsetMm: 150,
    baseRail: true,
    bracing: 'DIAG',
    rowBreakMm: 600,
    railInsetPct: 25,
    purlinOverhangMm: 0,
    ...PLATES,
    plateWMm: 120,
    plateLMm: 120,
    plateTMm: 6,
    anchorCount: '2',
    anchorDiaMm: 10,
    anchorEdgeMm: 25,
    ...COMMON,
    ...MOUNT,
  },
  ELEVATED_ROW: {
    tiltDeg: 10,
    tiltDir: 'ROOF',
    tiltAzimuthDeg: 180,
    heightMm: 2500,
    columnLayout: 'TWO',
    frameSpacingMm: 4000,
    endCantileverMm: 600,
    frontInsetMm: 300,
    rearInsetMm: 300,
    kneeBrace: true,
    kneeLengthMm: 500,
    bracing: 'END_BAYS',
    rowBreakMm: 600,
    railInsetPct: 25,
    purlinOverhangMm: 0,
    ...PLATES,
    ...COMMON,
    ...MOUNT,
  },
  ELEVATED_MULTI: {
    ...PLANE,
    columnSpacingUMm: 6000,
    columnSpacingVMm: 4000,
    cantileverUMm: 600,
    cantileverVMm: 600,
    rafterSpacingMm: 2000,
    kneeBrace: false,
    kneeLengthMm: 600,
    bracing: 'END_BAYS',
    ...SUPPORT,
    ...PLATES,
    plateWMm: 250,
    plateLMm: 250,
    plateTMm: 12,
    anchorDiaMm: 16,
    anchorEdgeMm: 50,
    ...COMMON,
    ...MOUNT,
  },
  PORTAL: {
    ...PLANE,
    heightMm: 3500,
    tiltDeg: 5,
    frameSpacingMm: 6000,
    frameCount: 0,
    cantileverUMm: 500,
    cantileverVMm: 800,
    kneeBrace: true,
    kneeLengthMm: 800,
    bracing: 'END_BAYS',
    ...SUPPORT,
    purlinMode: 'SPACING',
    purlinSpacingMm: 1150,
    ...PLATES,
    plateWMm: 300,
    plateLMm: 300,
    plateTMm: 16,
    anchorDiaMm: 20,
    anchorEdgeMm: 50,
    ...COMMON,
    ...MOUNT,
  },
  COLUMN_BEAM: {
    ...PLANE,
    tiltDeg: 0,
    heightMm: 3000,
    columnSpacingUMm: 5000,
    columnSpacingVMm: 5000,
    cantileverUMm: 500,
    cantileverVMm: 500,
    bracing: 'END_BAYS',
    ...SUPPORT,
    purlinMode: 'SPACING',
    purlinSpacingMm: 1150,
    ...PLATES,
    plateWMm: 300,
    plateLMm: 300,
    plateTMm: 16,
    anchorDiaMm: 20,
    anchorEdgeMm: 50,
    ...COMMON,
    ...MOUNT,
  },
  CUSTOM: {
    ...PLANE,
    lengthMm: 8000,
    widthMm: 5000,
    start: 'FRAME4',
    panelOffsetMm: 150,
    ...SUPPORT,
    purlinMode: 'SPACING',
    ...PLATES,
    ...COMMON,
    ...MOUNT,
  },
  CARPORT: {
    centerXMm: 0,
    centerYMm: 0,
    azimuthDeg: 180,
    lengthMm: 5400,
    widthMm: 5000,
    parkingBays: 2,
    roofType: 'MONO',
    slopeDeg: 5,
    heightMm: 2500,
    frontOverhangMm: 600,
    rearOverhangMm: 300,
    sideOverhangMm: 300,
    columnLayout: 'TWO',
    frameMode: 'PER_BAYS',
    baysPerFrame: 1,
    frameSpacingMm: 5000,
    kneeBrace: true,
    kneeLengthMm: 500,
    stayRod: false,
    stayMastMm: 1500,
    stayAtPct: 70,
    bracing: 'END_BAYS',
    clearHeightMinMm: 0,
    ...SUPPORT,
    purlinMode: 'SPACING',
    purlinSpacingMm: 1100,
    ...PLATES,
    ...COMMON,
    ...MOUNT,
  },
};

/** Default section per role (built-in illustrative sections, see sections.ts). */
export const DEFAULT_SECTIONS: Record<StructureKind, Partial<Record<MemberRole, string>>> = {
  ROOF_RAIL: { RAIL: 'RAIL-AL40x40', FOOT: 'LFOOT-AL40x40' },
  MINI_RAIL: { RAIL: 'MINIRAIL-AL45x40' },
  TILT_FRAME: {
    POST: 'SHS50x50x2',
    RAFTER: 'SHS50x50x2',
    PURLIN: 'RAIL-AL40x40',
    BASE_RAIL: 'SHS50x50x2',
    BRACE: 'L40x40x4',
    RAIL: 'RAIL-AL40x40',
  },
  ELEVATED_ROW: {
    COLUMN: 'SHS100x100x4',
    RAFTER: 'RHS100x50x3',
    PURLIN: 'C100x50x20x2',
    BRACE: 'L50x50x5',
    RAIL: 'RAIL-AL40x40',
  },
  ELEVATED_MULTI: {
    COLUMN: 'SHS125x125x5',
    BEAM: 'RHS150x100x4',
    RAFTER: 'RHS100x50x3',
    PURLIN: 'C100x50x20x2',
    BRACE: 'L50x50x5',
    RAIL: 'RAIL-AL40x40',
  },
  PORTAL: {
    COLUMN: 'H150x150x7x10',
    RAFTER: 'IPE200',
    BEAM: 'RHS150x100x4',
    PURLIN: 'C150x65x20x2.5',
    BRACE: 'L50x50x5',
    RAIL: 'RAIL-AL40x40',
  },
  COLUMN_BEAM: {
    COLUMN: 'SHS150x150x5',
    BEAM: 'RHS200x100x5',
    PURLIN: 'C150x65x20x2.5',
    BRACE: 'L63x63x6',
    RAIL: 'RAIL-AL40x40',
  },
  CUSTOM: {
    COLUMN: 'SHS100x100x4',
    BEAM: 'RHS150x100x4',
    PURLIN: 'C100x50x20x2',
    BRACE: 'L50x50x5',
    CUSTOM: 'RHS100x50x3',
    RAIL: 'RAIL-AL40x40',
  },
  CARPORT: {
    COLUMN: 'SHS100x100x4',
    BEAM: 'RHS150x100x4',
    RAFTER: 'RHS150x100x4',
    PURLIN: 'C100x50x20x2',
    BRACE: 'L50x50x5',
    RAIL: 'RAIL-AL40x40',
  },
};

/** Roles a kind generates (for the section editor). CUSTOM members may be added to any kind. */
export const KIND_ROLES: Record<StructureKind, MemberRole[]> = Object.fromEntries(
  Object.entries(DEFAULT_SECTIONS).map(([k, v]) => [k, Object.keys(v) as MemberRole[]]),
) as Record<StructureKind, MemberRole[]>;

export function paramDefs(kind: StructureKind): ParamDef[] {
  return PARAMS[kind];
}

/**
 * Kinds whose panel plane is lifted on columns above the roof: the panels' area is the frame (it may overhang the roof
 * and pass over roof obstacles / walkways); only the column feet must stand on the roof (validation).
 */
export function isElevatedStructure(kind: StructureKind): boolean {
  return ['ELEVATED_ROW', 'ELEVATED_MULTI', 'PORTAL', 'COLUMN_BEAM', 'CUSTOM'].includes(kind);
}

/** Kinds whose panel plane is tilted by the parameters (not parallel to the roof). */
export function isTiltedStructure(kind: StructureKind, params: Record<string, ParamValue>): boolean {
  if (kind === 'TILT_FRAME' || kind === 'ELEVATED_ROW') return true;
  return ['ELEVATED_MULTI', 'PORTAL', 'COLUMN_BEAM', 'CUSTOM'].includes(kind) && (params.tiltMode ?? DEFAULT_PARAMS[kind].tiltMode) === 'TILT';
}

/**
 * Compass bearing the tilted panels face when the tilt is turned away from the roof (LEFT / RIGHT / AZIMUTH); null when
 * the tilt follows the roof rows (ROOF / OPPOSITE, or a structure parallel to the roof).
 */
export function tiltFacingDeg(kind: StructureKind, stored: Record<string, ParamValue> | undefined, roofFaceDeg: number): number | null {
  const p = resolveParams(kind, stored);
  if (!isTiltedStructure(kind, p)) return null;
  const n360 = (x: number) => ((x % 360) + 360) % 360;
  if (p.tiltDir === 'LEFT') return n360(roofFaceDeg - 90);
  if (p.tiltDir === 'RIGHT') return n360(roofFaceDeg + 90);
  if (p.tiltDir === 'AZIMUTH') return n360(Number(p.tiltAzimuthDeg ?? 180));
  return null;
}

/** Whether a parameter is visible for the current values. */
export function paramVisible(def: ParamDef, values: Record<string, ParamValue>): boolean {
  return !def.showIf || def.showIf.in.includes(values[def.showIf.key]!);
}

/** Merge stored values over defaults, coercing types and clamping to the definitions. */
export function resolveParams(
  kind: StructureKind,
  stored: Record<string, ParamValue> | undefined,
): Record<string, ParamValue> {
  const out: Record<string, ParamValue> = { ...DEFAULT_PARAMS[kind] };
  for (const def of PARAMS[kind]) {
    const v = stored?.[def.key];
    if (v === undefined) continue;
    if (def.type === 'number') {
      const x = typeof v === 'number' ? v : Number(v);
      if (Number.isFinite(x)) out[def.key] = Math.min(def.max ?? x, Math.max(def.min ?? x, x));
    } else if (def.type === 'boolean') out[def.key] = v === true || v === 'true';
    else if (def.options?.some((o) => o.value === String(v))) out[def.key] = String(v);
  }
  return out;
}

/** Out-of-range / unknown stored values (for warnings). */
export function paramProblems(kind: StructureKind, stored: Record<string, ParamValue> | undefined): string[] {
  const out: string[] = [];
  for (const def of PARAMS[kind]) {
    const v = stored?.[def.key];
    if (v === undefined || def.type !== 'number') continue;
    const x = Number(v);
    if (
      !Number.isFinite(x) ||
      (def.min !== undefined && x < def.min) ||
      (def.max !== undefined && x > def.max)
    )
      out.push(
        `${def.label}: giá trị ${String(v)} ngoài khoảng ${def.min}–${def.max}${def.unit ? ` ${def.unit}` : ''}.`,
      );
  }
  return out;
}
